/**
 * ClientAPWorklist — Client-portal AP worklist and scoped site summaries.
 *
 * Shows the canonical-deduplicated actionable corrective-action worklist
 * for the signed-in client portal user, with:
 *
 *  L3 (site-assigned)   — Full CA worklist: owner, deadlines, original age,
 *                         awaiting-verification age, links to the AP detail.
 *  L1/L2 (read-only)    — Site-summary counts with drill-down links to plans.
 *
 * Carry-forward copies never inflate the count — each underlying action
 * appears exactly once (latest canonical version).
 *
 * Verification backlog: CAs where the client recorded completion but no
 * auditor verdict exists yet.  A scheduled visit is NOT a prerequisite.
 *
 * Route: /client/action-plans/worklist
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  ClipboardList,
  Loader2,
  RefreshCw,
  User,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { clientActionPlanService, clientPortfolioService } from '../services';
import { getApiError } from '@/lib/utils';
import { useClientPortal } from '../contexts/ClientPortalContext';

// ── State helpers ─────────────────────────────────────────────────────────────
// Headline groups (Issue 6): two primary states + overdue as a cross-cutting warning.
//   'awaiting_review' — client reported completion; no auditor verdict yet.
//   'to_do'           — all other unresolved (actionable, not_implemented, partially_implemented).
// 'Overdue' is NOT a fourth state — it is a deadline warning badge shown on
//  items in either group whenever by_when < today and not verified.

const HEADLINE_META = {
  awaiting_review: {
    label: 'Completed and awaiting auditor review',
    cls: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
    icon: Clock,
    iconCls: 'text-amber-500',
  },
  to_do: {
    label: 'To do',
    cls: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-400',
    icon: ClipboardList,
    iconCls: 'text-blue-500',
  },
};

/** Derive the headline group for a CA row returned by the worklist API. */
const caHeadlineGroup = ca =>
  ca.awaiting_verification ? 'awaiting_review' : 'to_do';

function OverdueBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-red-100 text-red-800 border-red-200"
      data-testid="overdue-badge"
    >
      <AlertTriangle className="w-3 h-3" />
      Overdue
    </span>
  );
}

function AgeTag({ days, label }) {
  if (days == null) return null;
  const colour =
    days > 90 ? 'text-red-600' :
    days > 30 ? 'text-amber-600' :
    'text-slate-500';
  return (
    <span className={`text-xs ${colour}`} title={label}>
      {label}: <strong>{days}d</strong>
    </span>
  );
}

// ── Verification backlog banner ───────────────────────────────────────────────

function VerificationBacklogBanner({ count }) {
  if (!count) return null;
  return (
    <div
      className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800"
      data-testid="verification-backlog-banner"
    >
      <Clock className="w-4 h-4 shrink-0 text-amber-500" />
      <span>
        <strong>{count} action{count !== 1 ? 's' : ''}</strong>
        {' '}completed and awaiting auditor review.
      </span>
    </div>
  );
}

// ── Worklist item row ─────────────────────────────────────────────────────────

function WorklistRow({ ca }) {
  const group = caHeadlineGroup(ca);
  return (
    <div
      className="bg-white rounded-lg border border-slate-200 p-4 flex flex-col gap-2 hover:border-slate-300 transition-colors"
      data-testid={`worklist-row-${ca.id}`}
    >
      {/* Top row: overdue badge (if applicable) + description */}
      <div className="flex items-start gap-3">
        {ca.is_overdue && <OverdueBadge />}
        <p className="text-sm text-slate-800 leading-snug flex-1 min-w-0">
          {ca.description}
        </p>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        {ca.by_who && (
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {ca.by_who}
          </span>
        )}
        {ca.by_when && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Due&nbsp;
            <span className={ca.is_overdue ? 'text-red-600 font-medium' : ''}>
              {ca.by_when}
            </span>
          </span>
        )}
        {ca.cf_original_by_when && ca.cf_original_by_when !== ca.by_when && (
          <span className="flex items-center gap-1 text-slate-400" title="Deadline from original action plan">
            <Calendar className="w-3 h-3" />
            Originally&nbsp;{ca.cf_original_by_when}
          </span>
        )}
        <AgeTag days={ca.original_age_days} label="Age" />
        {ca.awaiting_verification && ca.awaiting_verification_since_days != null && (
          <AgeTag days={ca.awaiting_verification_since_days} label="Awaiting" />
        )}
      </div>

      {/* Plan context + link (Issue 318: stock_number hidden from client worklist) */}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
        <span className="text-xs text-slate-400">
          {ca._site_name}{ca._scope ? ` · ${ca._scope}` : ''}
        </span>
        <Link
          to={`/client/action-plans/${ca._plan_id}`}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
          data-testid={`worklist-plan-link-${ca.id}`}
        >
          View plan →
        </Link>
      </div>
    </div>
  );
}

// ── Site summary row ──────────────────────────────────────────────────────────

function SiteRow({ site }) {
  const [expanded, setExpanded] = useState(false);
  const total = site.total_ca_count;
  if (total === 0) return null;

  const roleLabel = { L3: 'Site', L2: 'Regional', L1: 'Group' }[site.ap_role] || site.ap_role;
  const plans = site.plans || [];

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 p-4 flex flex-col gap-3"
      data-testid={`site-row-${site.site_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{site.site_name}</p>
          <p className="text-xs text-slate-400">{roleLabel} access</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {site.overdue_ca_count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-100 text-red-700 border border-red-200 rounded px-2 py-0.5" data-testid={`site-overdue-${site.site_id}`}>
              <AlertTriangle className="w-3 h-3" />{site.overdue_ca_count} overdue
            </span>
          )}
          {site.awaiting_verification_count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 rounded px-2 py-0.5" data-testid={`site-awaiting-${site.site_id}`}>
              <Clock className="w-3 h-3" />{site.awaiting_verification_count} awaiting auditor review
            </span>
          )}
          {(() => {
            const toDo = total - (site.awaiting_verification_count || 0);
            return toDo > 0 ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200 rounded px-2 py-0.5" data-testid={`site-todo-${site.site_id}`}>
                <ClipboardList className="w-3 h-3" />{toDo} to do
              </span>
            ) : null;
          })()}
          {plans.length > 0 && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              data-testid={`site-expand-plans-${site.site_id}`}
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {plans.length} plan{plans.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>

      {/* L1/L2 plan drill-down */}
      {expanded && plans.length > 0 && (
        <div className="pl-1 space-y-1 border-t border-slate-100 pt-2" data-testid={`site-plans-${site.site_id}`}>
          {plans.map(p => (
            <Link
              key={p.plan_id}
              to={`/client/action-plans/${p.plan_id}`}
              className="flex items-center justify-between text-xs text-slate-600 hover:text-indigo-700 py-0.5"
              data-testid={`site-plan-link-${p.plan_id}`}
            >
              <span>
                {p.scope}{p.audit_date ? ` · ${p.audit_date}` : ''}
              </span>
              <span className="text-indigo-400">→</span>
            </Link>
          ))}
          <p className="text-xs text-slate-400 italic pt-1">
            Read-only access — counts may overlap for overdue + awaiting actions
          </p>
        </div>
      )}
    </div>
  );
}

// ── Region summary row (L1 portfolio hierarchy) ───────────────────────────────

function RegionRow({ region, siteSummaries }) {
  const [expanded, setExpanded] = useState(false);

  // Only sites in this region that have actions
  const regionSites = siteSummaries.filter(
    s => region.site_ids.includes(s.site_id) && s.total_ca_count > 0
  );
  if (regionSites.length === 0) return null;

  // Sort by urgency within the region
  const sortedSites = [...regionSites].sort((a, b) =>
    ((b.overdue_ca_count || 0) + (b.awaiting_verification_count || 0)) -
    ((a.overdue_ca_count || 0) + (a.awaiting_verification_count || 0))
  );

  // Aggregate counts — unique per site so no double-counting
  const overdue  = regionSites.reduce((n, s) => n + (s.overdue_ca_count || 0), 0);
  const awaiting = regionSites.reduce((n, s) => n + (s.awaiting_verification_count || 0), 0);
  const total    = regionSites.reduce((n, s) => n + (s.total_ca_count || 0), 0);
  const siteCount = regionSites.length;

  return (
    <div
      className="border border-slate-200 rounded-lg overflow-hidden"
      data-testid={`region-row-${region.id}`}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        data-testid={`region-expand-${region.id}`}
        aria-expanded={expanded}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{region.name}</p>
          <p className="text-xs text-slate-400">
            {siteCount} site{siteCount !== 1 ? 's' : ''} · {total} action{total !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {overdue > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-100 text-red-700 border border-red-200 rounded px-2 py-0.5">
              <AlertTriangle className="w-3 h-3" />{overdue} overdue
            </span>
          )}
          {awaiting > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 rounded px-2 py-0.5">
              <Clock className="w-3 h-3" />{awaiting} awaiting auditor review
            </span>
          )}
          {expanded
            ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
            : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
        </div>
      </button>

      {expanded && (
        <div className="p-2 space-y-2 border-t border-slate-100" data-testid={`region-sites-${region.id}`}>
          {sortedSites.map(site => <SiteRow key={site.site_id} site={site} />)}
        </div>
      )}
    </div>
  );
}

// ── Headline-group section ────────────────────────────────────────────────────
// Groups: awaiting_review first (client is done, needs auditor); then to_do.
// Within 'to_do' the server has already sorted by urgency (overdue first, by_when asc).

const GROUP_ORDER  = ['awaiting_review', 'to_do'];

function WorklistSection({ group, items }) {
  const [open, setOpen] = useState(true);
  if (!items.length) return null;
  const meta = HEADLINE_META[group] || HEADLINE_META.to_do;
  const Icon = meta.icon;

  return (
    <section data-testid={`worklist-section-${group}`} className="space-y-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 w-full text-left group"
        aria-expanded={open}
      >
        <Icon className={`w-4 h-4 ${meta.iconCls}`} />
        <span className="text-sm font-semibold text-slate-700">
          {meta.label}
        </span>
        <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 ml-1">
          {items.length}
        </span>
        <span className="ml-auto text-slate-400 group-hover:text-slate-600">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="space-y-2 pl-6">
          {items.map(ca => <WorklistRow key={ca.id} ca={ca} />)}
        </div>
      )}
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ClientAPWorklist() {
  const navigate = useNavigate();
  const { apEnabled } = useClientPortal();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('worklist');
  const [siteQuery, setSiteQuery] = useState('');
  const [subRegions, setSubRegions] = useState([]);

  // Direct-URL guard: redirect to dashboard when AP is disabled.
  useEffect(() => {
    if (apEnabled === false) navigate('/client/dashboard', { replace: true });
  }, [apEnabled, navigate]);

  // All hooks must be declared before any conditional return (Rules of Hooks).
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    clientActionPlanService.worklist()
      .then(setData)
      .catch(e => setError(getApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  // Fetch sub-regions in parallel — scoped to the current user, used for L1 regional grouping.
  // Non-critical: if unavailable the Sites tab degrades to the flat urgency-sorted list.
  useEffect(() => {
    clientPortfolioService.getSubRegions()
      .then(d => setSubRegions(d?.sub_regions ?? []))
      .catch(() => {});
  }, []);

  const worklist = data?.worklist_items ?? [];
  const summaries = data?.site_summaries ?? [];
  const backlog = data?.verification_backlog_count ?? 0;

  // Group worklist items by headline state (Issue 6):
  //   awaiting_review — client done, no auditor verdict
  //   to_do           — all other unresolved (overdue, not_implemented, actionable)
  // Within each group preserve server sort order (overdue first, then by_when asc).
  const groups = { awaiting_review: [], to_do: [] };
  for (const ca of worklist) {
    groups[caHeadlineGroup(ca)].push(ca);
  }

  const hasWorklist = worklist.length > 0;
  const hasSummaries = summaries.some(s => s.total_ca_count > 0);
  // L1/L2 viewers have no worklist items — fall back to the Sites tab so the
  // content area never stays blank on the unrendered default 'worklist' tab.
  const activeTab = hasWorklist ? tab : 'sites';
  // Derive highest access role from site summaries for a scoped page heading
  const derivedRole = data
    ? (summaries.find(s => s.ap_role === 'L1') ? 'L1'
      : summaries.find(s => s.ap_role === 'L2') ? 'L2'
      : hasWorklist ? 'L3'
      : null)
    : null;
  const pageTitle = derivedRole === 'L1' ? 'Portfolio Action Overview'
    : derivedRole === 'L2' ? 'Regional Action Overview'
    : derivedRole === 'L3' ? 'Site Action Queue'
    : 'Action Overview';
  // Sort sites by urgency (overdue + awaiting highest first) for supervisory views
  const sortedSummaries = [...summaries].sort((a, b) =>
    ((b.overdue_ca_count || 0) + (b.awaiting_verification_count || 0)) -
    ((a.overdue_ca_count || 0) + (a.awaiting_verification_count || 0))
  );
  const filteredSummaries = siteQuery
    ? sortedSummaries.filter(s => s.site_name?.toLowerCase().includes(siteQuery.toLowerCase()))
    : sortedSummaries;
  // For L1 users with sub-region data: group sites into their regions
  const showRegionalHierarchy = derivedRole === 'L1' && subRegions.length > 0;

  // Loading gate: return nothing while branding resolves (apEnabled===null).
  // The guard useEffect above handles the apEnabled===false redirect.
  if (apEnabled === null) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="ap-worklist-page">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/client/action-plans"
          className="text-slate-400 hover:text-slate-600 transition-colors"
          data-testid="worklist-back-link"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{pageTitle}</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Each action is shown once, at its latest recorded point.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="ml-auto"
          data-testid="worklist-refresh-btn"
        >
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {/* Loading / error */}
      {loading && (
        <div className="flex justify-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}
      {error && (
        <div
          className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700"
          data-testid="worklist-error"
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Verification backlog banner */}
          <VerificationBacklogBanner count={backlog} />

          {/* Empty state */}
          {!hasWorklist && !hasSummaries && (
            <div
              className="text-center py-12 text-slate-400"
              data-testid="worklist-empty"
            >
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-300" />
              <p className="text-sm font-medium">No outstanding actions</p>
              <p className="text-xs mt-1">All corrective actions are resolved or none are assigned.</p>
            </div>
          )}

          {/* Tabs */}
          {(hasWorklist || hasSummaries) && (
            <div className="flex gap-1 border-b border-slate-200">
              {hasWorklist && (
                <button
                  onClick={() => setTab('worklist')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === 'worklist'
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="tab-worklist"
                >
                  Site Actions
                  <span className="ml-1.5 text-xs bg-indigo-100 text-indigo-700 rounded-full px-1.5 py-0.5">
                    {worklist.length}
                  </span>
                </button>
              )}
              {hasSummaries && (
                <button
                  onClick={() => setTab('sites')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === 'sites'
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="tab-sites"
                >
                  Sites
                  <span className="ml-1.5 text-xs bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">
                    {summaries.filter(s => s.total_ca_count > 0).length}
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Worklist tab */}
          {activeTab === 'worklist' && hasWorklist && (
            <div className="space-y-5" data-testid="worklist-tab-content">
              {GROUP_ORDER.map(group =>
                groups[group]?.length > 0 ? (
                  <WorklistSection key={group} group={group} items={groups[group]} />
                ) : null
              )}
            </div>
          )}

          {/* Sites tab */}
          {activeTab === 'sites' && hasSummaries && (
            <div className="space-y-2" data-testid="sites-tab-content">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search sites…"
                  value={siteQuery}
                  onChange={e => setSiteQuery(e.target.value)}
                  className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-300 bg-white"
                  data-testid="worklist-site-search"
                />
              </div>
              <p className="text-xs text-slate-500">
                Each action is counted once across all open plans for this site.
              </p>
              {showRegionalHierarchy ? (
                <>
                  {subRegions.map(region => (
                    <RegionRow
                      key={region.id}
                      region={region}
                      siteSummaries={filteredSummaries}
                    />
                  ))}
                  {/* Sites not assigned to any sub-region */}
                  {(() => {
                    const grouped = new Set(subRegions.flatMap(r => r.site_ids));
                    const others = filteredSummaries.filter(
                      s => !grouped.has(s.site_id) && s.total_ca_count > 0
                    );
                    return others.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500 font-medium px-1">Other</p>
                        {others.map(site => <SiteRow key={site.site_id} site={site} />)}
                      </div>
                    ) : null;
                  })()}
                  {filteredSummaries.length === 0 && siteQuery && (
                    <p className="text-xs text-slate-400 text-center py-4">No sites match.</p>
                  )}
                </>
              ) : (
                <>
                  {filteredSummaries.map(site => (
                    <SiteRow key={site.site_id} site={site} />
                  ))}
                  {filteredSummaries.length === 0 && siteQuery && (
                    <p className="text-xs text-slate-400 text-center py-4">No sites match.</p>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
